import React, { useState } from 'react';
import { siteConfig } from '../config/siteConfig';
import { Input } from './Input';
import { Label } from './Label';
import { Button } from './Button';
import { Checkbox } from './Checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './Select';
interface LeadFormProps {
  variant?: 'light' | 'dark';
}
export function LeadForm({
  variant = 'light'
}: LeadFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [adSpend, setAdSpend] = useState('');
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    const form = e.target as HTMLFormElement;
    const payload = {
      full_name: (form.elements.namedItem('fullName') as HTMLInputElement)?.value,
      email: (form.elements.namedItem('email') as HTMLInputElement)?.value,
      phone: (form.elements.namedItem('phone') as HTMLInputElement)?.value,
      med_spa_name: (form.elements.namedItem('spaName') as HTMLInputElement)?.value,
      monthly_ad_spend: adSpend
    };
    try {
      await fetch(siteConfig.form.webhookUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        mode: 'no-cors',
        body: JSON.stringify(payload)
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setIsSubmitting(false);
    }
  };
  if (submitted) {
    return <div className={`${variant === 'dark' ? 'bg-white/5 backdrop-blur-xl border-white/10 text-white' : 'bg-brand-cream border-gray-200 text-brand-navy'} p-8 rounded-[24px] shadow-2xl border text-center relative overflow-hidden`} data-id="element-99">
        {variant === 'dark' && <div className="absolute inset-0 bg-brand-gold/5 mix-blend-overlay" data-id="element-100"></div>}
        <div className="relative z-10" data-id="element-101">
          <div className="w-16 h-16 bg-green-500/20 text-green-400 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-102">
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24" data-id="element-103">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" data-id="element-104" />
            </svg>
          </div>
          <h3 className="text-2xl font-bold mb-2" data-id="element-105">Request Received</h3>
          <p className={variant === 'dark' ? 'text-gray-300' : 'text-muted-foreground'} data-id="element-106">
            We will be in touch shortly to schedule your strategy call.
          </p>
        </div>
      </div>;
  }
  const isDark = variant === 'dark';
  return <div className={`${isDark ? 'bg-white/5 backdrop-blur-xl border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.4)]' : 'bg-brand-cream border-gray-200 shadow-xl'} p-6 md:p-8 rounded-[24px] border relative`} id="lead-form" data-id="element-107">
      {isDark && <div className="absolute -inset-0.5 bg-gradient-to-b from-white/10 to-transparent rounded-[24px] -z-10 blur-[2px]" data-id="element-108"></div>}

      <div className="mb-8" data-id="element-109">
        <h3 className={`text-2xl font-bold mb-2 ${isDark ? 'text-white' : 'text-brand-navy'}`} data-id="element-110">
          Get Our Team to Set This Up For You
        </h3>
        <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-muted-foreground'}`} data-id="element-111">
          Book a free 20 minute strategy call. We will review your current ad
          tracking and show you exactly what to fix.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5" data-id="element-112">
        <div className="space-y-2" data-id="element-113">
          <Label htmlFor="fullName" className={isDark ? 'text-gray-300' : ''} data-id="element-114">
            Full Name *
          </Label>
          <Input id="fullName" name="fullName" required placeholder="Jane Doe" className={`min-h-[48px] ${isDark ? 'bg-white/10 border-white/10 text-white placeholder:text-gray-500 focus-visible:ring-brand-gold' : 'bg-white'}`} data-id="element-115" />
        </div>

        <div className="space-y-2" data-id="element-116">
          <Label htmlFor="email" className={isDark ? 'text-gray-300' : ''} data-id="element-117">
            Email *
          </Label>
          <Input id="email" name="email" type="email" required placeholder="jane@medspa.com" className={`min-h-[48px] ${isDark ? 'bg-white/10 border-white/10 text-white placeholder:text-gray-500 focus-visible:ring-brand-gold' : 'bg-white'}`} data-id="element-118" />
        </div>

        <div className="space-y-2" data-id="element-119">
          <Label htmlFor="phone" className={isDark ? 'text-gray-300' : ''} data-id="element-120">
            Phone Number *
          </Label>
          <Input id="phone" name="phone" type="tel" required placeholder="(555) 000-0000" className={`min-h-[48px] ${isDark ? 'bg-white/10 border-white/10 text-white placeholder:text-gray-500 focus-visible:ring-brand-gold' : 'bg-white'}`} data-id="element-121" />
        </div>

        <div className="space-y-2" data-id="element-122">
          <Label htmlFor="spaName" className={isDark ? 'text-gray-300' : ''} data-id="element-123">
            Med Spa Name *
          </Label>
          <Input id="spaName" name="spaName" required placeholder="Luxe Med Spa" className={`min-h-[48px] ${isDark ? 'bg-white/10 border-white/10 text-white placeholder:text-gray-500 focus-visible:ring-brand-gold' : 'bg-white'}`} data-id="element-124" />
        </div>

        <div className="space-y-2" data-id="element-125">
          <Label htmlFor="adSpend" className={isDark ? 'text-gray-300' : ''} data-id="element-126">
            Monthly Ad Spend *
          </Label>
          <Select required onValueChange={setAdSpend} data-id="element-127">
            <SelectTrigger className={`min-h-[48px] ${isDark ? 'bg-white/10 border-white/10 text-white focus:ring-brand-gold' : 'bg-white'}`} data-id="element-128">
              <SelectValue placeholder="Select monthly spend" data-id="element-129" />
            </SelectTrigger>
            <SelectContent className={isDark ? 'bg-brand-navy border-white/10 text-white' : ''} data-id="element-130">
              {siteConfig.form.adSpendOptions.map(opt => <SelectItem key={opt} value={opt} className={isDark ? 'focus:bg-white/10 focus:text-white' : ''} data-id="element-131">
                  {opt}
                </SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-4 pt-4" data-id="element-132">
          <div className="flex items-start space-x-3" data-id="element-133">
            <Checkbox id="a2p-trans" className={`mt-1 ${isDark ? 'border-white/30 data-[state=checked]:bg-brand-gold data-[state=checked]:text-brand-navy' : ''}`} data-id="element-134" />
            <Label htmlFor="a2p-trans" className={`text-xs font-normal leading-relaxed cursor-pointer ${isDark ? 'text-gray-400' : 'text-muted-foreground'}`} data-id="element-135">
              I agree to receive transactional messages from Let Lois related to
              my inquiry. Message and data rates may apply. Reply STOP to opt
              out. *
            </Label>
          </div>

          <div className="flex items-start space-x-3" data-id="element-136">
            <Checkbox id="a2p-mktg" className={`mt-1 ${isDark ? 'border-white/30 data-[state=checked]:bg-brand-gold data-[state=checked]:text-brand-navy' : ''}`} data-id="element-137" />
            <Label htmlFor="a2p-mktg" className={`text-xs font-normal leading-relaxed cursor-pointer ${isDark ? 'text-gray-400' : 'text-muted-foreground'}`} data-id="element-138">
              I agree to receive marketing messages from Let Lois with offers
              and updates. Message and data rates may apply. Reply STOP to opt
              out.
            </Label>
          </div>
        </div>

        <div className="pt-4" data-id="element-139">
          <p className={`text-[10px] text-center mb-6 ${isDark ? 'text-gray-500' : 'text-muted-foreground'}`} data-id="element-140">
            By submitting this form you agree to our{' '}
            <a href={siteConfig.links.privacy} target="_blank" rel="noopener noreferrer" className="underline hover:text-brand-gold" data-id="element-141">
              Privacy Policy
            </a>{' '}
            and{' '}
            <a href={siteConfig.links.terms} target="_blank" rel="noopener noreferrer" className="underline hover:text-brand-gold" data-id="element-142">
              Terms of Service
            </a>
            .
          </p>

          <Button type="submit" disabled={isSubmitting} className="w-full min-h-[56px] text-base font-bold bg-brand-gold hover:bg-brand-gold/90 text-brand-navy shadow-lg" data-id="element-143">
            {isSubmitting ? 'Submitting...' : 'Book My Strategy Call'}
          </Button>
        </div>
      </form>
    </div>;
}